import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../../../stores/authStore';

export interface RealtimeEvent {
    id: string;
    type: string;
    category: string;
    userId?: string;
    timestamp: string;
    location?: {
        located?: boolean;
        reason?: string;
        lat: number;
        lng: number;
        city: string;
        region?: string;
        postal?: string;
    };
}

export interface ActiveVisitor {
    id: string;
    location: {
        located?: boolean;
        reason?: string;
        lat: number;
        lng: number;
        city: string;
        region?: string;
        postal?: string;
    };
    firstActivity: string;
    lastActivity: string;
    lastEventType: string;
    deviceType: string;
    userId?: string;
    userType?: 'anonymous' | 'new_signup' | 'existing_user';
}

export interface FeatureUsage {
    pageStats: {
        type: string;
        userCount: number;
        boardCount: number;
    }[];
    chartStats: {
        type: string;
        count: number;
    }[];
}

export interface TodayStats {
    signups: number;
    totalEvents: number;
    activeNow: number;
}

export const useAnalyticsWebSocket = () => {
    const token = useAuthStore(state => state.tokens?.accessToken);
    const refreshTokens = useAuthStore(state => state.refreshTokens);
    const [connected, setConnected] = useState(false);
    const [recentEvents, setRecentEvents] = useState<RealtimeEvent[]>([]);
    const [activeVisitors, setActiveVisitors] = useState<ActiveVisitor[]>([]);
    const [todayStats, setTodayStats] = useState<TodayStats>({
        signups: 0,
        totalEvents: 0,
        activeNow: 0,
    });
    const [featureUsage, setFeatureUsage] = useState<FeatureUsage | null>(null);

    const wsRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const connect = useCallback(async (isRetry = false) => {
        if (!token || wsRef.current) return;

        // Clear any pending reconnect
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        // Use runtime config for WS URL if available, otherwise construct from current origin
        const config = (window as any).__DASHBAL_CONFIG__;
        const configWsUrl = config?.wsUrl;
        const apiBaseUrl = config?.apiBaseUrl;
        let wsUrl: string;

        if (configWsUrl) {
            // Ensure token is appended to the configured URL
            const separator = configWsUrl.includes('?') ? '&' : '?';
            wsUrl = `${configWsUrl}${separator}token=${token}`;
        } else if (apiBaseUrl) {
            // Derive from API base URL
            const wsBase = apiBaseUrl.replace(/\/$/, '').replace(/^http/, 'ws');
            wsUrl = `${wsBase}/ws/analytics?token=${token}`;
        } else {
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const host = window.location.host.includes('localhost') ? 'localhost:3001' : window.location.host;
            wsUrl = `${protocol}//${host}/ws/analytics?token=${token}`;
        }

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
            setConnected(true);
            console.log('Analytics WebSocket connected');
        };

        ws.onmessage = (message) => {
            try {
                const data = JSON.parse(message.data);
                const { event, data: payload } = data;

                switch (event) {
                    case 'analytics:initial_state':
                        setRecentEvents(payload.recentEvents);
                        setActiveVisitors(payload.activeVisitors);
                        setTodayStats(payload.todayStats);
                        if (payload.featureUsage) setFeatureUsage(payload.featureUsage);
                        break;

                    case 'analytics:event':
                        setRecentEvents(prev => [payload.event, ...prev].slice(0, 50));
                        setTodayStats(prev => ({
                            ...prev,
                            totalEvents: prev.totalEvents + 1,
                            activeNow: payload.activeNow,
                        }));
                        break;

                    case 'analytics:signup':
                        setTodayStats(prev => ({
                            ...prev,
                            signups: prev.signups + 1,
                        }));
                        break;

                    case 'analytics:session_update':
                        setTodayStats(prev => ({
                            ...prev,
                            activeNow: payload.activeNow,
                        }));
                        if (payload.sessions) {
                            setActiveVisitors(payload.sessions);
                        }
                        break;
                }
            } catch (err) {
                console.error('Failed to parse analytics WS message', err);
            }
        };

        ws.onclose = async (event) => {
            setConnected(false);
            wsRef.current = null;
            console.log('Analytics WebSocket disconnected', event.code, event.reason);

            // If it was an authentication failure (4001) and we haven't retried with a fresh token yet
            if (event.code === 4001 && !isRetry) {
                try {
                    console.log('Attempting token refresh after WS auth failure...');
                    await refreshTokens();
                    // The effect will re-trigger connect() because the token in store changed
                    return;
                } catch (err) {
                    console.error('Failed to refresh token after WS auth failure', err);
                }
            }

            // Normal reconnection logic
            reconnectTimeoutRef.current = setTimeout(() => connect(), 5000);
        };

        ws.onerror = (err) => {
            console.error('Analytics WebSocket error', err);
        };
    }, [token, refreshTokens]);

    useEffect(() => {
        connect();
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, [connect]);

    const refresh = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close(); // Closing will trigger reconnect logic which fetches initial state
        } else {
            connect();
        }
    }, [connect]);

    return {
        connected,
        recentEvents,
        activeVisitors,
        todayStats,
        featureUsage,
        refresh,
    };
};
